// A price field that lets you type a price.
//
// These were controlled inputs holding cents and rendering
// `(cents / 100).toFixed(2)`, which reformats on every keystroke. Typing
// "75" into a field showing "0.00" went: "7" -> 700 cents -> redrawn as
// "7.00" with the caret at the end -> "5" appended as "7.005" -> 701 cents
// -> "7.01". The field fought every digit after the first, and the only
// way to enter a round number was to fight back.
//
// The fix is to stop reformatting mid-edit. While the field has focus it
// holds exactly what was typed; the parsed value still flows out on every
// keystroke, so nothing downstream has to wait for blur. Tidying to two
// decimals happens on the way out.
import React, { useEffect, useRef, useState } from 'react';

interface Props {
  valueCents: number;
  onChange: (cents: number) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Render empty rather than "0.00" when the value is zero. */
  blankWhenZero?: boolean;
}

const toText = (cents: number, blankWhenZero?: boolean): string =>
  blankWhenZero && !cents ? '' : (cents / 100).toFixed(2);

/**
 * Dollars to cents, tolerant of half-typed input.
 *
 * "", ".", "7." and "-" are all things a field legitimately contains
 * partway through typing a number; each parses to 0 rather than NaN.
 */
const toCents = (text: string): number => {
  const n = Number(text);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
};

const MoneyInput: React.FC<Props> = ({
  valueCents,
  onChange,
  placeholder,
  className,
  ariaLabel,
  disabled,
  blankWhenZero,
}) => {
  const [text, setText] = useState(() => toText(valueCents, blankWhenZero));
  const focused = useRef(false);

  // Follow the value when it changes underneath us -- a form reset, a
  // server round-trip -- but never while someone is mid-word.
  useEffect(() => {
    if (!focused.current) setText(toText(valueCents, blankWhenZero));
  }, [valueCents, blankWhenZero]);

  return (
    <input
      // Not type="number": its spinners, locale-dependent decimal handling
      // and silent rejection of intermediate values cause their own
      // trouble. inputMode gets the numeric keypad on a phone regardless.
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      className={className}
      onFocus={e => {
        focused.current = true;
        // Select what's there so typing replaces it. Without this, a field
        // showing "0.00" keeps that text and a click near the front turns
        // "75" into "750.00" -- the digits land in front of a zero that
        // was never meant to be part of the number.
        e.target.select();
      }}
      onChange={e => {
        // Digits and at most one decimal point. Anything else is dropped
        // rather than rejected, so a stray keypress doesn't clear the field.
        const cleaned = e.target.value
          .replace(/[^\d.]/g, '')
          .replace(/^(\d*\.?\d*).*$/, '$1');
        setText(cleaned);
        onChange(toCents(cleaned));
      }}
      onBlur={() => {
        focused.current = false;
        setText(toText(valueCents, blankWhenZero));
      }}
    />
  );
};

export default MoneyInput;
